'use client'

import { useEffect, useState } from 'react'
import { AlertCircle, Camera, CheckCircle2, Loader2, UserCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

type User = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  callsign: string | null
  hamCallsign: string | null
  gmrsCallsign: string | null
  radios: string | null
  showReactionAttribution: boolean
}

export function AccountForm() {
  const [user, setUser] = useState<User | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [handle, setHandle] = useState('')
  const [hamCallsign, setHamCallsign] = useState('')
  const [gmrsCallsign, setGmrsCallsign] = useState('')
  const [radios, setRadios] = useState('')
  const [showReactionAttribution, setShowReactionAttribution] = useState(true)
  const [avatarData, setAvatarData] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    fetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const u = data?.user ?? null
        setUser(u)
        setHandle(u?.handle ?? '')
        setHamCallsign(u?.hamCallsign ?? u?.callsign ?? '')
        setGmrsCallsign(u?.gmrsCallsign ?? '')
        setRadios(u?.radios ?? '')
        setShowReactionAttribution(u?.showReactionAttribution ?? true)
      })
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  const inputClass =
    'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30'

  async function saveHandle(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setNotice('')
    setSaving(true)
    try {
      const res = await fetch('/api/forum/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle,
          hamCallsign,
          gmrsCallsign,
          radios,
          showReactionAttribution,
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        error?: string
        handle?: string
        hamCallsign?: string
        gmrsCallsign?: string
        radios?: string
        showReactionAttribution?: boolean
      }
      if (!res.ok) throw new Error(data.error || 'Could not save your handle.')
      setNotice('Forum profile updated.')
      setUser((prev) =>
        prev
          ? {
              ...prev,
              handle: data.handle ?? handle,
              hamCallsign: data.hamCallsign ?? hamCallsign,
              gmrsCallsign: data.gmrsCallsign ?? gmrsCallsign,
              radios: data.radios ?? radios,
              showReactionAttribution:
                data.showReactionAttribution ?? showReactionAttribution,
            }
          : prev,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your handle.')
    } finally {
      setSaving(false)
    }
  }

  async function uploadAvatar() {
    if (!avatarData) return
    setError('')
    setNotice('')
    setSaving(true)
    try {
      const res = await fetch('/api/forum/avatar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: avatarData }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string; avatarPath?: string }
      if (!res.ok) {
        let message = data.error
        if (!message) {
          if (res.status === 413) message = 'That image is too large. Please use one under 2 MB.'
          else if (res.status === 400)
            message = 'That file is not a supported image. Use a JPEG, PNG, GIF, or WebP.'
          else message = 'The avatar upload failed. Please try again.'
        }
        throw new Error(message)
      }
      setNotice('Avatar updated.')
      setAvatarData(null)
      setUser((prev) => (prev ? { ...prev, avatarPath: data.avatarPath ?? null } : prev))
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not reach the server. Check your connection and try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowed.includes(file.type)) {
      setError('Use a JPEG, PNG, GIF, or WebP image.')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Avatar must be under 2 MB.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setAvatarData(String(reader.result || ''))
    reader.readAsDataURL(file)
  }

  if (!loaded) return null

  if (!user) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Please{' '}
          <a href="/login" className="text-primary underline underline-offset-4">
            log in
          </a>{' '}
          to manage your forum profile.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">Avatar</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          JPEG, PNG, GIF, or WebP, up to 2 MB. Shown next to your posts.
        </p>
        <div className="mt-4 flex items-center gap-4">
          {avatarData || user.avatarPath ? (
            <img
              src={avatarData || user.avatarPath || ''}
              alt="Your avatar"
              className="h-16 w-16 rounded-full border border-border object-cover"
            />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-border bg-background text-primary">
              <UserCircle2 className="h-8 w-8" />
            </span>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary">
              <Camera className="h-4 w-4" />
              Choose image
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} />
            </label>
            {avatarData && (
              <Button type="button" variant="outline" onClick={uploadAvatar} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Upload
              </Button>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={saveHandle} className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold">Forum profile</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your handle is required. Amateur and GMRS callsigns and your radio lineup are optional
          and shown to other forum members.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Handle</span>
          <input
            type="text"
            required
            minLength={3}
            maxLength={24}
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="your-handle"
            className={inputClass}
          />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Amateur callsign (optional)</span>
            <input
              type="text"
              maxLength={20}
              value={hamCallsign}
              onChange={(e) => setHamCallsign(e.target.value)}
              placeholder="W1ABC"
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">GMRS callsign (optional)</span>
            <input
              type="text"
              maxLength={20}
              value={gmrsCallsign}
              onChange={(e) => setGmrsCallsign(e.target.value)}
              placeholder="WQ1ABC"
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-medium">Radio lineup (optional)</span>
            <input
              type="text"
              maxLength={300}
              value={radios}
              onChange={(e) => setRadios(e.target.value)}
              placeholder="Baofeng UV-5R, Yaesu FT-60R, Wouxun KG-935G"
              className={inputClass}
            />
          </label>
          <label className="flex items-start gap-3 rounded-lg border border-border bg-background p-3 sm:col-span-2">
            <input
              type="checkbox"
              checked={showReactionAttribution}
              onChange={(e) => setShowReactionAttribution(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              <span className="block text-sm font-medium">
                Show my handle in positive reaction lists
              </span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                Applies to Likes, Helpful, QSL, and Worked it! Dislike attribution is never public
                and is visible only to moderators for abuse investigations.
              </span>
            </span>
          </label>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save profile
          </Button>
        </div>
      </form>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 text-sm text-primary">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}
    </div>
  )
}
